// Per-asset Automation panel — the primary entry point for configuring interlocks.
//
// An engineer adding an underground tank configures its logic right here, looking at the device
// they just wired, rather than hunting for it on a separate page. Shows every rule where this
// asset is an input, a permissive, an output or a feedback point.
//
// DROP-IN COMPONENT — not yet mounted anywhere. To add it to the asset registry, render
// <AssetAutomationPanel asset={editing} /> inside AssetRegistry.jsx's edit dialog. It is
// self-sufficient: give it an asset and it loads everything else itself.

import React, { useCallback, useEffect, useState } from "react";
import { Plus, Zap, Loader2, ServerCrash } from "lucide-react";
import { Button } from "../ui/button";
import { automationApi } from "../../lib/automationApi";
import { assetRoleInRule } from "../../lib/automationModel";
import { useAutomationCatalogue } from "./useAutomationCatalogue";
import { SafetyClassBadge, ModeBadge, Chip } from "./badges";
import RuleSentence from "./RuleSentence";
import RuleBuilder from "./RuleBuilder";

const ROLE_LABELS = {
  input: "triggers this rule",
  permissive: "gates this rule",
  output: "driven by this rule",
  feedback: "confirms this rule",
};

export default function AssetAutomationPanel({ asset, canEdit = true }) {
  const siteId = asset?.site_id;
  const { ctx, liveValues } = useAutomationCatalogue(siteId);

  const [rules, setRules] = useState([]);
  const [unavailable, setUnavailable] = useState(false);
  const [loading, setLoading] = useState(true);
  const [builderOpen, setBuilderOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const load = useCallback(async () => {
    if (!asset?.asset_id) return;
    setLoading(true);
    const r = await automationApi.listRules({ site_id: siteId, asset_id: asset.asset_id });
    setUnavailable(r.unavailable);
    setRules(r.ok ? r.data || [] : []);
    setLoading(false);
  }, [asset, siteId]);

  useEffect(() => { load(); }, [load]);

  const save = async (rule) => {
    setSaving(true);
    setSaveError(null);
    const r = rule.rule_id
      ? await automationApi.updateRule(rule.rule_id, rule)
      : await automationApi.createRule(rule);
    setSaving(false);
    if (r.unavailable) {
      setSaveError("The automation API is not deployed on this server yet, so the rule was not saved. The logic you built is valid — see SPEC_AUTOMATION_BACKEND_CHANGES.md.");
      return;
    }
    if (!r.ok) {
      setSaveError(r.error);
      return;
    }
    setBuilderOpen(false);
    setEditing(null);
    load();
  };

  if (!asset?.asset_id) return null;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-orange-600 font-bold flex items-center gap-1">
            <Zap className="w-3 h-3" strokeWidth={2.5} /> Automation
          </div>
          <p className="text-xs text-gray-500 mt-0.5">
            Cause &amp; effect rules that watch or drive {asset.name}.
          </p>
        </div>
        {canEdit && !unavailable && (
          <Button
            size="sm"
            className="bg-orange-600 hover:bg-orange-700 text-white text-xs"
            onClick={() => { setEditing(null); setBuilderOpen(true); }}
          >
            <Plus className="w-3.5 h-3.5 mr-1" /> New rule
          </Button>
        )}
      </div>

      {unavailable ? (
        <div className="flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3">
          <ServerCrash className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={2.5} />
          <span>
            The automation API is not deployed on this server yet. Rules cannot be listed or saved until the
            backend in <code className="font-mono">SPEC_AUTOMATION_BACKEND_CHANGES.md</code> ships.
          </span>
        </div>
      ) : loading ? (
        <div className="flex items-center gap-2 text-xs text-gray-500 py-4">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading rules…
        </div>
      ) : rules.length === 0 ? (
        <div className="text-xs text-gray-500 border border-dashed border-gray-300 rounded-lg p-4 text-center">
          No automation configured for this device.
          <div className="text-[11px] text-gray-400 mt-1">
            Rules are never created automatically — every one is configured by hand against the devices at this site.
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          {rules.map((rule) => {
            const role = assetRoleInRule(rule, asset.asset_id);
            return (
              <div key={rule.rule_id} className="border border-gray-200 rounded-lg bg-white p-3">
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-bold text-sm">{rule.name}</span>
                    <SafetyClassBadge value={rule.safety_class} />
                    <ModeBadge value={rule.mode} />
                    {role && <Chip tone="gray">{ROLE_LABELS[role]}</Chip>}
                  </div>
                  {canEdit && (
                    <button
                      type="button"
                      className="text-xs text-orange-600 hover:text-orange-700 font-semibold"
                      onClick={() => { setEditing(rule); setBuilderOpen(true); }}
                    >
                      Edit
                    </button>
                  )}
                </div>
                <RuleSentence rule={rule} ctx={ctx} className="mt-2" />
              </div>
            );
          })}
        </div>
      )}

      <RuleBuilder
        open={builderOpen}
        onOpenChange={(v) => { setBuilderOpen(v); if (!v) { setEditing(null); setSaveError(null); } }}
        siteId={siteId}
        initialRule={editing}
        ctx={ctx}
        liveValues={liveValues}
        onSave={save}
        saving={saving}
        saveError={saveError}
      />
    </div>
  );
}
